/**
 * Processing status management utilities
 * Handles processing status changes and queue events
 */

import { setCurrentlyProcessingSession, updateWorkflowSessionState } from '../store/reducers';
import { clearWorkflowFiles } from './workflowFileCache';
import { tryDispatchProcessNextInQueue } from './workflowStreamStatusUtils';

/**
 * Handle processing status change and dispatch queue events
 */
export const handleProcessingStatusChange = ({
  processingStatus,
  prevProcessingStatus,
  modalWorkflowSessionId,
  setAreStepsCompleted,
  setCurrentStep,
  setCompletedSteps,
  setHasError,
  setIsProcessing,
  setIsMinimized,
  dispatch,
  workflowSessions,
  hasDispatchedQueueEventRef,
  errorHandledRef,
}) => {
  if (prevProcessingStatus === processingStatus) {
    return { statusHandled: false, shouldUpdateSteps: false };
  }

  if (processingStatus === 'completed') {
    setAreStepsCompleted(true);
    setCurrentStep(2);
    setCompletedSteps([0, 1, 2]);
    setHasError(false);
    errorHandledRef.current = false;
    setIsProcessing(false);
    setIsMinimized(false);
    
    if (modalWorkflowSessionId && dispatch) {
      dispatch(updateWorkflowSessionState(modalWorkflowSessionId, {
        isProcessing: false,
        stepsCompleted: true,
      }));
      
      // PERFORMANCE: Clear PDFs from workflow cache after processing completes
      // This frees up memory since PDFs are no longer needed after processing
      clearWorkflowFiles(modalWorkflowSessionId);
      
      console.log(`✅ [Workflow:${modalWorkflowSessionId}] Workflow completed in queue`);
    }

    // CRITICAL: Always clear currentlyProcessingSessionId when workflow completes This must happen BEFORE checking if we should dispatch the queue event
    dispatch(setCurrentlyProcessingSession(null));
    
    if (modalWorkflowSessionId) {
      const sent = tryDispatchProcessNextInQueue(modalWorkflowSessionId);
      hasDispatchedQueueEventRef.current = true;
      if (process.env.NODE_ENV === 'development' && !sent) {
        console.log(`⏸️ [Workflow:${modalWorkflowSessionId}] processNextInQueue skipped (already sent for this completion)`);
      }
    }

    return { statusHandled: true, shouldUpdateSteps: true };
  } 
  
  if (processingStatus === 'processing') {
    const store = require('../store/store').default || require('../store/store');
    const currentState = store.getState();
    const workflowSessions = currentState?.workflowSessions || {};
    const session = workflowSessions?.sessions?.[modalWorkflowSessionId];
    const isAlreadyCompleted = session?.state?.stepsCompleted || false;
    
    if (isAlreadyCompleted) {
      return { statusHandled: false, shouldUpdateSteps: false };
    }

    setCurrentStep(1);
    setCompletedSteps([0]);
    setHasError(false);
    errorHandledRef.current = false;

    return { statusHandled: true, shouldUpdateSteps: true };
  } 
  
  if (processingStatus === 'error') {
    if (!errorHandledRef.current) {
      errorHandledRef.current = true;
      setHasError(true);
      setIsProcessing(false);
      setIsMinimized(false);
      return { statusHandled: true, shouldUpdateSteps: false };
    }
    return { statusHandled: false, shouldUpdateSteps: false };
  }

  return { statusHandled: false, shouldUpdateSteps: false };
};

